import { formatInTimeZone } from "date-fns-tz";
import {
  BookableRef,
  Extra,
  LockType,
  Rate,
  RestrictionAvailability,
  RestrictionBlockage,
  RestrictionCode,
  RestrictionMaxNotice,
  RestrictionMaxStay,
  RestrictionMinNotice,
  RestrictionMinStay,
  RestrictionTravelWindow,
  StayDaysFilterType,
  Type,
  Variant,
  Multimedia,
  GuestType,
} from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";
import {
  ExtraApplyTo,
  ExtraLockType,
  ExtraRateType,
  ExtraRestrictionUse,
  ExtraType,
  ExtraUse,
  RangeDateWindowType,
  TranslationType,
  GuestType as GuestTypeEnum,
} from "../../types";
import { CustomExtra } from "../types";
import { ExtraCategorizationMapper } from "../../../extrasCategories/infrastructure/mappers/ExtraCategorizationMapper";
import { mapExtraCategorizationToForm } from "../../../extrasCategories/presentation/mappers/extra-category-form.mapper";
import { MultimediaTranslation } from "../../../../gen/witbooking/backoffice/common/common_pb";
import { Image, Translation as ImageTranslation } from "../../../shared/widgets/ImageManager";

export const mapToObjectFromGRPC = (
  extraGRPC: Extra.AsObject,
  allowedLocales: string[]
): CustomExtra => {
  return {
    id: extraGRPC.id,
    ticker: extraGRPC.ticker,
    active: extraGRPC.active,
    visible: extraGRPC.visible,
    applyTo: mapStayDaysFilterType(extraGRPC.stayDaysFilterType),
    applyToGuests: mapApplyToGuests(extraGRPC.appliesToGuestsList),
    availability: mapRestrictionAvailability(extraGRPC.restrictions?.availability),
    blockage: mapRestrictionBlockage(extraGRPC.restrictions?.blockage),
    inventoryLinesList: mapInventoryLinesList(extraGRPC.appliesToList),
    chainInventoryLinesList: [],
    emailsList: mapNotificationEmailsList(extraGRPC.notificationEmailsList),
    lockType: mapLockType(extraGRPC.lockType),
    maxNoticeType: mapMaxNoticeType(extraGRPC.restrictions?.maxNotice),
    maxNotice: mapMaxNotice(extraGRPC.restrictions?.maxNotice),
    maxStayType: mapMaxStayType(extraGRPC.restrictions?.maxStay),
    maxStay: mapMaxStay(extraGRPC.restrictions?.maxStay),
    minNoticeType: mapMinNoticeType(extraGRPC.restrictions?.minNotice),
    minNotice: mapMinNotice(extraGRPC.restrictions?.minNotice),
    minStayType: mapMinStayType(extraGRPC.restrictions?.minStay),
    minStay: mapMinStay(extraGRPC.restrictions?.minStay),
    multimediaList: mapMultimediaList(extraGRPC.multimediaList),
    ratePrice: mapRatePrice(extraGRPC.rate),
    rateType: mapRateType(extraGRPC.rate),
    type: mapType(extraGRPC.type, extraGRPC.variant),
    codesList: mapCodesList(extraGRPC.restrictions?.code),
    travelWindow: mapTravelWindow(extraGRPC.restrictions?.travelWindow),
    translationsList: mapTranslations(extraGRPC.translationsList, allowedLocales),
    maxUnits: mapMaxBookableUnits(extraGRPC.maxBookableUnits),
    parentPropertyTicker: mapParentPropertyTicker(extraGRPC.parentPropertyTicker),
    tax: extraGRPC.tax,
    ...mapExtraCategorizationToForm(ExtraCategorizationMapper.toDomain(extraGRPC.categorization)),
  };
};

const mapTranslations = (translationsList: TranslationType[], allowedLocales: string[]) => {
  return allowedLocales.map((locale: string) => {
    const element = translationsList.find((item: TranslationType) => item.locale === locale);
    return element
      ? { locale, name: element?.name, description: element?.description }
      : { locale, name: "", description: "" };
  });
};

const mapStayDaysFilterType = (type: StayDaysFilterType) => {
  switch (type) {
    case StayDaysFilterType.ONLY_CHECKIN:
      return ExtraApplyTo.EXTRA_APPLY_TO_ONLY_CHECKIN;
    case StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKIN:
      return ExtraApplyTo.EXTRA_APPLY_TO_ALL_STAY_DAYS_EXCEPT_CHECKIN;
    case StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKOUT:
      return ExtraApplyTo.EXTRA_APPLY_TO_ALL_STAY_DAYS_EXCEPT_CHECKOUT;
    default:
      throw new Error(`applyTo [${type}] not supported`);
  }
};

const mapApplyToGuests = (guestTypes: GuestType[]) => {
  return guestTypes.map(it => {
    switch (it) {
      case GuestType.ADULT:
        return GuestTypeEnum.ADULT;
      case GuestType.BABY:
        return GuestTypeEnum.BABY;
      case GuestType.CHILD:
        return GuestTypeEnum.CHILD;
      case GuestType.SENIOR:
        return GuestTypeEnum.SENIOR;
      case GuestType.TEENAGER:
        return GuestTypeEnum.TEENAGER;
      default:
        throw new Error(`guest type [${it}] not supported`);
    }
  });
};

const mapRestrictionAvailability = (availability?: RestrictionAvailability.AsObject) => {
  if (availability?.value?.own) {
    return ExtraUse.EXTRA_USE_OWN;
  }
  return ExtraUse.EXTRA_USE_UNKNOWN;
};

const mapRestrictionBlockage = (blockage?: RestrictionBlockage.AsObject) => {
  if (blockage?.value?.own) {
    return ExtraUse.EXTRA_USE_OWN;
  }
  return ExtraUse.EXTRA_USE_UNKNOWN;
};

const mapNotificationEmailsList = (emailsList: string[]) => {
  return emailsList.map(it => it);
};

const mapType = (type: Type, variant: Variant) => {
  switch (variant) {
    case Variant.PER_DAY:
      switch (type) {
        case Type.PERSON:
          return ExtraType.EXTRA_TYPE_PERSON_PER_DAY;
        case Type.ROOM:
          return ExtraType.EXTRA_TYPE_ROOM_PER_DAY;
        case Type.UNIT:
          return ExtraType.EXTRA_TYPE_UNIT_PER_DAY;
        default:
          throw new Error(`type [${type}] not supported`);
      }
    case Variant.PER_DAY_MANDATORY:
      switch (type) {
        case Type.PERSON:
          return ExtraType.EXTRA_TYPE_PERSON_PER_DAY_MANDATORY;
        case Type.ROOM:
          return ExtraType.EXTRA_TYPE_ROOM_PER_DAY_MANDATORY;
        case Type.UNIT:
          return ExtraType.EXTRA_TYPE_UNIT;
        default:
          throw new Error(`type [${type}] not supported`);
      }
    case Variant.MANDATORY:
      switch (type) {
        case Type.PERSON:
          return ExtraType.EXTRA_TYPE_PERSON_MANDATORY;
        case Type.ROOM:
          return ExtraType.EXTRA_TYPE_ROOM_MANDATORY;
        case Type.UNIT:
          return ExtraType.EXTRA_TYPE_UNIT;
        default:
          throw new Error(`type [${type}] not supported`);
      }
    case Variant.SPECIFIC_DATE:
      switch (type) {
        case Type.PERSON:
          return ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE;
        case Type.ROOM:
          return ExtraType.EXTRA_TYPE_ROOM_SPECIFIC_DATE;
        case Type.UNIT:
          return ExtraType.EXTRA_TYPE_UNIT_SPECIFIC_DATE;
        default:
          throw new Error(`type [${type}] not supported`);
      }
    case Variant.UNKNOWN:
    default:
      switch (type) {
        case Type.PERSON:
          return ExtraType.EXTRA_TYPE_PERSON;
        case Type.ROOM:
          return ExtraType.EXTRA_TYPE_ROOM;
        case Type.UNIT:
          return ExtraType.EXTRA_TYPE_UNIT;
        default:
          throw new Error(`type [${type}] not supported`);
      }
  }
};

const mapLockType = (lockType: LockType) => {
  switch (lockType) {
    case LockType.DAY:
      return ExtraLockType.EXTRA_LOCK_TYPE_DAY;
    case LockType.STAY:
      return ExtraLockType.EXTRA_LOCK_TYPE_STAY;
    default:
      throw new Error(`lockType [${lockType}] not supported`);
  }
};

const mapRateType = (rate?: Rate.AsObject) => {
  if (rate?.own) {
    return ExtraRateType.EXTRA_RATE_TYPE_OWN;
  }
  if (rate?.constant) {
    return ExtraRateType.EXTRA_RATE_TYPE_CONSTANT;
  }
  return ExtraRateType.EXTRA_RATE_TYPE_UNKNOWN;
};

const mapRatePrice = (rate?: Rate.AsObject) => {
  if (rate?.constant) {
    return rate.constant.value;
  }
  return 0;
};

const mapMaxNoticeType = (maxNotice?: RestrictionMaxNotice.AsObject) => {
  if (maxNotice?.value?.constant) {
    return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT;
  }
  return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN;
};

const mapMaxNotice = (maxNotice?: RestrictionMaxNotice.AsObject) => {
  if (maxNotice?.value?.constant) {
    return maxNotice.value.constant.simpleDuration?.value;
  }
};

const mapMinNoticeType = (minNotice?: RestrictionMinNotice.AsObject) => {
  if (minNotice?.value?.constant) {
    return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT;
  }
  return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN;
};

const mapMinNotice = (minNotice?: RestrictionMinNotice.AsObject) => {
  if (minNotice?.value?.constant) {
    return minNotice.value.constant.simpleDuration?.value;
  }
};

const mapMinStayType = (minStay?: RestrictionMinStay.AsObject) => {
  if (minStay?.value?.constant) {
    return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT;
  }
  return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN;
};

const mapMinStay = (minStay?: RestrictionMinStay.AsObject) => {
  if (minStay?.value?.constant) {
    return (minStay.value.constant.minStayDuration?.value || 0) / 24;
  }
};

const mapMaxStayType = (maxStay?: RestrictionMaxStay.AsObject) => {
  if (maxStay?.value?.constant) {
    return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT;
  }
  return ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN;
};

const mapMaxStay = (maxStay?: RestrictionMaxStay.AsObject) => {
  if (maxStay?.value?.constant) {
    return (maxStay.value.constant.maxStayDuration?.value || 0) / 24;
  }
};

const mapCodesList = (code?: RestrictionCode.AsObject) => {
  return code?.value?.constant?.unlockCodes?.codesList.map(it => it.code) || [];
};

const mapInventoryLinesList = (inventoryLinesList: BookableRef.AsObject[]): string[] => {
  const inventoryLines = inventoryLinesList.map(it => it.inventory);
  return inventoryLines.map(it => it?.tickersList || []).flat();
};

const mapMultimediaList = (multimediaList: Multimedia.AsObject[]) => {
  return multimediaList.map(({ id, filename, url, translationsList }) => {
    return ({
      id,
      name: filename,
      filename,
      url,
      translations: translationsList.reduce((acc, { locale, title, description }) => {
        acc[locale] = { title, description };
        return acc;
      }, {} as MultimediaTranslation.AsObject),
    } as unknown) as Image;
  });
};

const mapTravelWindow = (travelWindow?: RestrictionTravelWindow.AsObject): RangeDateWindowType => {
  const period = travelWindow?.value?.constant?.dateWindow?.periodList.pop();
  return {
    startDate: getProtoTimestampAsDateStringSecond(period?.dateRange?.startDate),
    endDate: getProtoTimestampAsDateStringSecond(period?.dateRange?.endDate),
  };
};

const mapMaxBookableUnits = (maxBookableUnits?: number) => {
  return maxBookableUnits?.toString();
};

const mapParentPropertyTicker = (parentPropertyTicker?: string) => {
  return parentPropertyTicker && parentPropertyTicker.length > 0 ? parentPropertyTicker : undefined;
};

export const transformMultimediaToImageManageObject = (
  translationList: ImageTranslation
): MultimediaTranslation.AsObject[] => {
  return Object.keys(translationList).map((locale: string) => {
    const { title, description } = translationList[locale];
    return { title, description, locale } as MultimediaTranslation.AsObject;
  });
};

function getProtoTimestampAsDateStringSecond(
  timestamp: { seconds: number; nanos: number },
  format: string = "yyyy-MM-dd"
): string | null {
  if (!timestamp || timestamp.seconds === 0) {
    return null;
  }
  // @ts-ignore
  const protoDate = new proto.google.protobuf.Timestamp();
  protoDate.setSeconds(timestamp.seconds);
  protoDate.setNanos(timestamp.nanos);
  return formatInTimeZone(protoDate.toDate(), "Europe/Madrid", format, {
    timeZone: "Europe/Madrid",
  });
}
